// :cow: Cosmose CONFIDENTIAL :iso:
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';

import * as d3 from 'd3';
import * as d3Sankey from 'd3-sankey';
import { SankeyData } from '@coa/app/shared/sankey-diagram/components/models/sankeyData';

@Component({
  selector: 'coa-sankey-diagram',
  templateUrl: './sankey-diagram.component.html',
  styleUrls: ['./sankey-diagram.component.scss'],
})
export class SankeyDiagramComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: SankeyData[];
  @Input() label: string;

  @ViewChild('parent') parent: ElementRef<HTMLElement>;
  @ViewChild('sankeyChart') sankeyChart: ElementRef<HTMLElement>;
  @ViewChild('sankeyTooltip') sankeyTooltip: ElementRef<HTMLElement>;
  public sankeyTooltipText: string;
  private param: number;
  private isReady = false;
  private readonly activeTooltipClass = 'active';
  private readonly defaultStrokeColor = '#fff';
  private readonly activeStrokeColor = 'rgba(0, 145, 230)';
  private readonly tooltipTopClass = 'top';
  private readonly tooltipBottomClass = 'bottom';
  private eventListeners: { element: HTMLElement, type: string, callback: EventListenerOrEventListenerObject }[] = [];

  public constructor(
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.countParameter();
  }

  ngOnDestroy(): void {
    this.unbindAll();
  }

  ngOnChanges(): void {
    if (this.isReady) {
      this.unbindAll();
      this.redrawChart();
      this.bindChartEvents();
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.isReady = true;
    this.unbindAll();
    this.redrawChart();
    this.bindChartEvents();
  }

  private bindChartEvents(): void {
    this.sankeyChart.nativeElement.querySelectorAll('#sankey path')
      .forEach((element: HTMLElement, index) => {
        this.bind(element, index);
      });
  }

  private bind(element: HTMLElement, index: number): void {
    const mouseMoveCallback = (event: any) => {
      const offset = 60;
      let tooltipOffset: number;
      let tooltipClassToAdd: string;
      let tooltipClassToRemove: string;
      if (event.layerY <= offset) {
        tooltipClassToAdd = this.tooltipBottomClass;
        tooltipClassToRemove = this.tooltipTopClass;
        tooltipOffset = 40;
      } else {
        tooltipClassToAdd = this.tooltipTopClass;
        tooltipClassToRemove = this.tooltipBottomClass;
        tooltipOffset = -60;
      }
      this.sankeyTooltip.nativeElement.classList.add(this.activeTooltipClass);
      this.sankeyTooltip.nativeElement.classList.add(tooltipClassToAdd);
      this.sankeyTooltip.nativeElement.classList.remove(tooltipClassToRemove);
      element.style.stroke = this.activeStrokeColor;
      this.sankeyTooltip.nativeElement.style.left = `${ event.layerX }px`;
      this.sankeyTooltip.nativeElement.style.top = `${ event.layerY + tooltipOffset }px`;
      const brandPreference = this.data[index];
      this.sankeyTooltipText = `${ brandPreference.brandName } ${ brandPreference.referenceGroupVisitorsPercentage }%`;
    };
    this.addEventListener(element, 'mousemove', mouseMoveCallback);

    const mouseOutCallback = (): void => {
      this.sankeyTooltip.nativeElement.classList.remove(this.activeTooltipClass);
      element.style.stroke = this.defaultStrokeColor;
    };
    this.addEventListener(element, 'mouseout', mouseOutCallback);
  }

  private addEventListener(element: HTMLElement, type: string, callback: EventListenerOrEventListenerObject): void {
    element.addEventListener(type, callback);
    const entry = {element, type, callback};
    this.eventListeners.push(entry);
  }

  private unbindAll(): void {
    this.eventListeners.forEach(entry => {
      entry.element.removeEventListener(entry.type, entry.callback);
    });
    this.eventListeners = [];
  }

  private redrawChart(): void {
    this.countParameter();
    const svg = d3.select('#sankey');
    svg.selectAll('*').remove();

    function checkColor(d: any, clientBrand): any {
      if (d.name === clientBrand) {
        return '#5A7AFF';
      } else {
        return 'rgba(255,255,255,0.36)';
      }
    }

    const parentWidth = this.parent.nativeElement.offsetWidth;
    this.sankeyChart.nativeElement.setAttribute('width', parentWidth.toString());
    this.cdr.detectChanges();

    if (svg) {
      const width = svg.attr('width');
      const height = svg.attr('height');

      d3.format(',.0f');


      const sankey = d3Sankey.sankey()
        .nodeWidth(32)
        .nodePadding(10)
        .extent([[1, 1], [width - 1, height - 6]])
        .linkSort(null);

      const link = svg.append('g')
        .attr('class', 'links')
        .attr('fill', 'none')
        .attr('stroke', 'white')
        .attr('stroke-opacity', 0.1)
        .selectAll('path');

      let node = svg.append('g')
        .attr('class', 'nodes')
        .attr('font-family', 'Rubik')
        .attr('font-size', 16)
        .attr('fill', 'rgba(rgba(255,255,255,0.5)')
        .selectAll('g');

      const energy: DAG = {
        nodes: this.createNodes(),
        links: this.createLinks(),
      };

      sankey(energy);

      link
        .data(energy.links)
        .enter().append('path')
        .attr('d', d3Sankey.sankeyLinkHorizontal())
        .attr('stroke-width', (d: any) => Math.max(1, d.width));

      node = node
        .data(energy.nodes)
        .enter().append('g');

      node.append('rect')
        .attr('x', d => {
          return d.x0;
        })
        .attr('y', d => {
          return d.y0;
        })
        .attr('height', d => {
          return d.y1 - d.y0;
        })
        .attr('width', d => {
          return d.x1 - d.x0;
        })
        .attr('fill', d => {
          return checkColor(d, this.label);
        });

      node.append('text')
        .attr('x', (d: any) => d.x0 - 6)
        .attr('y', (d: any) => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('fill', 'rgba(255,255,255,0.5)')
        .attr('font-size', '14px')
        .attr('text-anchor', 'end')
        .text((d: any) => d.name)
        .filter((d: any) => d.x0 < width / 2)
        .attr('x', (d: any) => d.x1 + 6)
        .attr('fill', 'white')
        .attr('font-family', 'Rubik, sans-serif')
        .attr('font-size', '16px')
        .attr('text-anchor', 'start');
    }
  }

  private countParameter(): void {
    const sum = this.data.map(brand => brand['referenceGroupVisitorsPercentage']).reduce((el, prev) => el + prev);
    this.param = sum * 4 / this.data[0]['referenceGroupVisitorsPercentage'];
  }

  private createNodes(): any {
    let nodes: any[] = [{
      nodeId: 0,
      name: this.label,
    }];

    this.data.forEach((brand, index) => {
      const node = {nodeIs: index + 1, name: brand.brandName};
      nodes = [...nodes, node];
    });
    return nodes;
  }

  private createLinks(): any {
    return this.data.map((brand, index) => {
      return {
        source: 0,
        target: index + 1,
        value: brand['referenceGroupVisitorsPercentage'],
        rawValue: brand['referenceGroupVisitorsPercentage'],
      };
    });
  }
}

interface SNodeExtra {
  nodeId: number;
  name: string;
}

interface SLinkExtra {
  source: number;
  target: number;
  value: number;
  uom: string;
}

type SNode = d3Sankey.SankeyNode<SNodeExtra, SLinkExtra>;
type SLink = d3Sankey.SankeyLink<SNodeExtra, SLinkExtra>;

interface DAG {
  nodes: SNode[];
  links: SLink[];
}

